import { addDays, addMinutes, isSameDay, startOfDay } from "date-fns";
import {
  CHAT_SCHEDULE_MAX_LEAD_MS,
  CHAT_SCHEDULE_MIN_LEAD_MS,
} from "@/types/(my-space)/chat.interface";

/**
 * "Schedule message" quick picks, in the user's local time. Pure: `now` is a
 * parameter so the edge cases (midnight, Sunday) are testable. The mobile app
 * offers the same presets with the same rules.
 */
export type SchedulePresetKey =
  | "later_today"
  | "tomorrow_morning"
  | "tomorrow_afternoon"
  | "next_monday";

export interface SchedulePreset {
  key: SchedulePresetKey;
  at: Date;
}

const LATER_TODAY_HOURS = 3;
const MORNING_HOUR = 9;
const AFTERNOON_HOUR = 14;
const MONDAY = 1;

/** `day` at hh:00, local time. */
const atHour = (day: Date, hour: number) => {
  const d = startOfDay(day);
  d.setHours(hour);
  return d;
};

/** Up to the next :00 or :30; a time already on one stays put. */
export const roundUpToHalfHour = (date: Date) => {
  const d = new Date(date);
  const onBoundary =
    d.getMinutes() % 30 === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;
  if (onBoundary) return d;
  d.setSeconds(0, 0);
  return addMinutes(d, 30 - (d.getMinutes() % 30));
};

export const schedulePresets = (now: Date): SchedulePreset[] => {
  const presets: SchedulePreset[] = [];

  // now + 3h, rounded up to the half hour — only while it's still today.
  const later = roundUpToHalfHour(
    new Date(now.getTime() + LATER_TODAY_HOURS * 60 * 60 * 1000),
  );
  if (isSameDay(later, now)) presets.push({ key: "later_today", at: later });

  const tomorrow = addDays(startOfDay(now), 1);
  presets.push({ key: "tomorrow_morning", at: atHour(tomorrow, MORNING_HOUR) });
  presets.push({ key: "tomorrow_afternoon", at: atHour(tomorrow, AFTERNOON_HOUR) });

  // The coming Monday (a week ahead on a Monday); "Tomorrow" already covers Sunday.
  if (tomorrow.getDay() !== MONDAY) {
    const days = (MONDAY - now.getDay() + 7) % 7 || 7;
    presets.push({
      key: "next_monday",
      at: atHour(addDays(startOfDay(now), days), MORNING_HOUR),
    });
  }
  return presets;
};

export type SendAtError = "invalid" | "past" | "too_far";

/** Mirrors the API: at least a minute ahead, at most a year. */
export const validateSendAt = (at: Date | null, now: Date): SendAtError | null => {
  if (!at || Number.isNaN(at.getTime())) return "invalid";
  const lead = at.getTime() - now.getTime();
  if (lead < CHAT_SCHEDULE_MIN_LEAD_MS) return "past";
  if (lead > CHAT_SCHEDULE_MAX_LEAD_MS) return "too_far";
  return null;
};

/** The custom picker's starting value: an hour out, on the half hour. */
export const defaultCustomSendAt = (now: Date) =>
  roundUpToHalfHour(new Date(now.getTime() + 60 * 60 * 1000));

/** Local date + "HH:mm" (a `type="time"` value) → one Date; null if incomplete. */
export const combineDateAndTime = (date: Date | undefined, time: string) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!date || !match) return null;
  const d = startOfDay(date);
  d.setHours(Number(match[1]), Number(match[2]));
  return d;
};

/** Date → the "HH:mm" a `type="time"` input holds (data, not display). */
export const timeInputValue = (date: Date) =>
  `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
